// src/pages/dashboard/DashboardSekretaris.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  FileText,
  CheckCircle,
  Gavel,
  ArrowUpRight,
  BarChart3,
  Plus,
  AlertTriangle,
  HelpCircle,
  Info,
  XCircle,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Loading, Button } from '../../components/ui';
import { useCapabilities } from '../../hooks/useCapabilities';
import { TingkatRisiko } from '../../types';
import api from '../../services/api';
import {
  getRapimQueueAlertCopy,
  getRapimQueueSectionCopy,
  isRapimUlangItem,
  resolveRapimQueueSummary,
} from '../../utils/rapimQueueSummary';

const DashboardSekretaris: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [rapimList, setRapimList] = useState<any[]>([]);
  // âœ… FIX: Tambah state untuk daftar laporan antrean RAPIM
  const [antreanLaporan, setAntreanLaporan] = useState<any[]>([]);
  const [showGuide, setShowGuide] = useState(false);

  const caps = useCapabilities();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dashRes, rapimRes, antreanRes] = await Promise.all([
          api.get('/dashboard', { params: { context: 'monitoring' } }),
          api.get('/rapim', { params: { per_page: 10 } })
            .catch(() => ({ data: { data: [] } })),
          // âœ… FIX: Fetch daftar laporan yang menunggu dijadwalkan RAPIM
          api.get('/rapim/available-laporan', { params: { per_page: 10 } })
            .catch(() => ({ data: { data: [] } })),
        ]);

        setData(dashRes.data?.data || dashRes.data);

        // Parse rapim meetings
        const rapimPayload = rapimRes.data?.data || rapimRes.data || [];
        const rapimItems = Array.isArray(rapimPayload)
          ? rapimPayload
          : Array.isArray((rapimPayload as any)?.data)
            ? (rapimPayload as any).data
            : [];
        setRapimList(rapimItems);

        // âœ… FIX: Parse daftar laporan antrean RAPIM
        const antreanPayload = antreanRes.data?.data || antreanRes.data || [];
        setAntreanLaporan(
          Array.isArray(antreanPayload) ? antreanPayload : []
        );
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Loading variant="page" message="Memuat dashboard..." />;

  // Access guard
  if (!caps.hasAny(['schedule_rapim', 'manage_rapim', 'access_monitoring', 'view_all_reports'])) {
    return (
      <div className="space-y-6 animate-in">
        <Breadcrumb items={[{ label: 'Dashboard' }]} />
        <Card className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-warning" />
          <p className="text-lg font-bold">Akses Terbatas</p>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Anda tidak memiliki capabilities yang diperlukan.
          </p>
        </Card>
      </div>
    );
  }

  const lps = data?.laporan_per_status || {};
  const antreanRapimSummary = resolveRapimQueueSummary(
    data?.antrean_rapim_summary,
    antreanLaporan
  );
  const antreanRapimAlertCopy = getRapimQueueAlertCopy(antreanRapimSummary);
  const antreanRapimSectionCopy = getRapimQueueSectionCopy(antreanRapimSummary);
  const antreanRapimCardTitle = antreanRapimSummary.dikembalikan_ke_rapim > 0
    && antreanRapimSummary.baru_disetujui_warek === 0
    ? 'Laporan Dikembalikan ke RAPIM'
    : antreanRapimSummary.baru_disetujui_warek > 0
      && antreanRapimSummary.dikembalikan_ke_rapim === 0
      ? 'Laporan Menunggu Rapat Pimpinan'
      : 'Antrean & RAPIM Ulang';

  // Untuk Sekretaris, yang dihitung di sini hanya laporan yang benar-benar
  // belum masuk ke rapat aktif dan masih menunggu penjadwalan.
  const antreanRapimCount = antreanRapimSummary.total;

  const resolvedLocalCount = data?.resolved_local_count || 0;
  const totalSelesai = (lps.selesai || 0) + (lps.diarsipkan || 0) + resolvedLocalCount;
  const totalLaporan = data?.total_laporan || 0;
  const tindakLanjutAktif = (lps.berlangsung || 0) + (lps.menunggu_konfirmasi || 0);

  const rapimDijadwalkan = rapimList.filter((r: any) =>
    ['dijadwalkan', 'berlangsung'].includes(r.status)
  );

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: 'Sekretaris' }]} />

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard Sekretaris
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Kelola penjadwalan Rapat Pimpinan dan pantau laporan universitas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => setShowGuide((prev) => !prev)}
          >
            Panduan
          </Button>
          {caps.canScheduleRapim && (
            <Link to="/rapim/buat">
              <button className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-main text-white text-sm font-semibold rounded-lg hover:bg-teal-light shadow-md transition-all">
                <Plus className="w-4 h-4" />
                Jadwalkan RAPIM
              </button>
            </Link>
          )}
        </div>
      </div>

      {showGuide && (
        <Card className="border border-teal-main/10 bg-gradient-to-r from-white via-white to-teal-main/5 animate-in">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-main/10 shrink-0">
                <Info className="w-5 h-5 text-teal-main" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-dark">Cara membaca dashboard Sekretaris</h2>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Panduan singkat untuk membantu Sekretaris membaca prioritas penjadwalan RAPIM dan ringkasan kondisi laporan universitas.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="text-neutral-text-secondary hover:text-teal-main transition-colors shrink-0"
              aria-label="Tutup panduan dashboard Sekretaris"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Menunggu Dijadwalkan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menunjukkan jumlah laporan yang menunggu penjadwalan RAPIM, baik laporan baru yang sudah disetujui Wakil Rektor maupun laporan yang dikembalikan ke RAPIM untuk pembahasan ulang.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Rapat Akan Datang</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan rapat yang telah dijadwalkan atau sedang berlangsung, sehingga Sekretaris dapat memantau agenda rapat terdekat.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Laporan Tuntas</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Mencakup laporan yang selesai melalui RAPIM, laporan yang diarsipkan, dan laporan yang diselesaikan langsung pada level pimpinan yang berwenang.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Antrean RAPIM</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Blok ini menampilkan daftar laporan individual yang menunggu penjadwalan, lengkap dengan risiko dan penanda RAPIM ulang jika ada.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Ringkasan untuk Pimpinan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menyajikan gambaran singkat kondisi laporan universitas yang sudah tuntas, masih aktif ditindaklanjuti, atau telah melewati target waktu.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* âœ… FIX: Alert jika ada laporan menunggu RAPIM */}
      {antreanRapimCount > 0 && caps.hasAny(['manage_rapim', 'schedule_rapim']) && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="p-2 bg-purple-100 rounded-lg shrink-0">
            <Gavel className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-purple-900">
              {antreanRapimCardTitle}
            </h3>
            <p className="text-sm text-purple-800 mt-1">
              {antreanRapimAlertCopy.description}
            </p>
          </div>
          {caps.canScheduleRapim && (
            <Link to="/rapim/buat">
              <button className="whitespace-nowrap px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                Jadwalkan Rapat
              </button>
            </Link>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {caps.hasAny(['manage_rapim', 'schedule_rapim']) && (
          <Card padding="sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-100">
                <Gavel className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-purple-600">
                  {antreanRapimCount}
                </p>
                <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">
                  Menunggu Dijadwalkan
                </p>
              </div>
            </div>
          </Card>
        )}

        {caps.hasAny(['manage_rapim', 'schedule_rapim']) && (
          <Card padding="sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-info/10">
                <Calendar className="w-5 h-5 text-info" />
              </div>
              <div>
                <p className="text-2xl font-black text-info">
                  {rapimDijadwalkan.length}
                </p>
                <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">
                  Rapat Akan Datang
                </p>
              </div>
            </div>
          </Card>
        )}

        {caps.canAccessMonitoring && (
          <>
            <Card padding="sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-success/10">
                  <CheckCircle className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-black text-success">{totalSelesai}</p>
                  <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">
                    Laporan Tuntas
                  </p>
                </div>
              </div>
            </Card>
            <Card padding="sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-teal-main/10">
                  <FileText className="w-5 h-5 text-teal-main" />
                </div>
                <div>
                  <p className="text-2xl font-black text-teal-main">{totalLaporan}</p>
                  <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">
                    Total Laporan Univ.
                  </p>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rapat Mendatang */}
        {caps.hasAny(['manage_rapim', 'schedule_rapim']) && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
                <Calendar className="w-5 h-5 text-info" />
                Rapat Mendatang
              </h3>
              <Link
                to="/rapim"
                className="text-xs font-semibold text-teal-main hover:underline"
              >
                Semua RAPIM
              </Link>
            </div>

            {rapimDijadwalkan.length > 0 ? (
              <div className="space-y-3">
                {rapimDijadwalkan.slice(0, 5).map((rapat: any) => (
                  <Link key={rapat.id} to={`/rapim/${rapat.id}`}>
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-neutral-border hover:border-teal-main/30 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-info/10 text-info flex items-center justify-center shrink-0">
                        <Calendar className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">
                          {rapat.judul}
                        </p>
                        <p className="text-xs text-neutral-text-secondary">
                          {formatDate(rapat.tanggal)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${rapat.status === 'berlangsung'
                          ? 'bg-success/10 text-success'
                          : 'bg-info/10 text-info'
                          }`}
                      >
                        {rapat.status === 'berlangsung'
                          ? 'Berlangsung'
                          : 'Dijadwalkan'}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
                <p className="text-sm text-neutral-text-secondary">
                  Belum ada rapat dijadwalkan
                </p>
                {caps.canScheduleRapim && (
                  <Link
                    to="/rapim/buat"
                    className="text-xs text-teal-main font-semibold hover:underline mt-2 inline-block"
                  >
                    Jadwalkan rapat baru  →
                  </Link>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Antrean RAPIM */}
        {caps.hasAny(['manage_rapim', 'schedule_rapim']) && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
                <Gavel className="w-5 h-5 text-purple-600" />
                {antreanRapimCardTitle}
              </h3>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                {antreanRapimCount} laporan
              </span>
            </div>
            <p className="text-xs text-neutral-text-secondary mb-3">
              {antreanRapimSectionCopy}
            </p>

            {antreanLaporan.length > 0 ? (
              <div className="space-y-2">
                {antreanLaporan.slice(0, 5).map((lap: any, i: number) => (
                  <Link
                    key={lap.id || i}
                    to={`/laporan/${lap.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-purple-100 bg-purple-50/30 hover:bg-purple-50 hover:border-purple-200 transition-colors"
                  >
                    <div className="p-2 rounded-lg bg-purple-100 shrink-0">
                      <Gavel className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono text-teal-main">
                          {lap.kode}
                        </span>
                        <Badge
                          variant="risk"
                          risk={
                            (lap.tingkat_risiko ||
                              lap.risiko ||
                              'sedang') as TingkatRisiko
                          }
                        />
                        {/*Indikator RAPIM ulang */}
                        {isRapimUlangItem(lap) && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold">
                            RAPIM Ulang
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate">
                        {lap.judul}
                      </p>
                      <p className="text-xs text-neutral-text-secondary">
                        {lap.unit?.name || '-'} •{' '}
                        {lap.kategori_masalah?.nama ||
                          lap.kategoriMasalah?.nama ||
                          '-'}
                      </p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-neutral-text-secondary shrink-0" />
                  </Link>
                ))}

                {antreanRapimCount > 5 && (
                  <Link
                    to="/rapim/buat"
                    className="block text-center text-xs font-semibold text-purple-600 hover:underline pt-2"
                  >
                    Lihat semua {antreanRapimCount} laporan →
                  </Link>
                )}

                {caps.canScheduleRapim && (
                  <Link to="/rapim/buat" className="block mt-3">
                    <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors">
                      Jadwalkan RAPIM untuk Membahas
                    </button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-success opacity-50" />
                <p className="text-sm text-neutral-text-secondary">
                  Tidak ada laporan dalam antrean
                </p>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Ringkasan */}
      {caps.hasAny(['access_monitoring', 'view_all_reports']) && (
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Ringkasan untuk Pimpinan
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-teal-main/5 rounded-xl text-center">
              <p className="text-3xl font-black text-teal-main">{totalLaporan}</p>
              <p className="text-xs text-neutral-text-secondary mt-1">Total Laporan</p>
            </div>
            <div className="p-4 bg-success/5 rounded-xl text-center">
              <p className="text-3xl font-black text-success">{totalSelesai}</p>
              <p className="text-xs text-neutral-text-secondary mt-1">Selesai</p>
            </div>
            <div className="p-4 bg-warning/5 rounded-xl text-center">
              <p className="text-3xl font-black text-warning">{tindakLanjutAktif}</p>
              <p className="text-xs text-neutral-text-secondary mt-1">Tindak Lanjut Aktif</p>
            </div>
            <div className="p-4 bg-danger/5 rounded-xl text-center">
              <p className="text-3xl font-black text-danger">
                {data?.laporan_overdue || 0}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1">Terlambat</p>
            </div>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!caps.hasAny([
        'manage_rapim',
        'schedule_rapim',
        'access_monitoring',
        'view_all_reports',
      ]) && (
          <Card className="text-center py-12">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-neutral-text-secondary opacity-40" />
            <p className="text-sm font-semibold text-neutral-text-primary">
              Tidak ada data untuk ditampilkan
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Hubungi administrator untuk mendapatkan capabilities yang sesuai.
            </p>
          </Card>
        )}
    </div>
  );
};

export default DashboardSekretaris;

